import Phaser from 'phaser';
import { CANVAS_W, PlayerId } from '../types';
import { GameScene } from './GameScene';
import { AudioManager } from '../systems/AudioManager';

/**
 * Overlay scene that draws the per-player HUD (lives, score, powerups) plus the
 * shared level label + countdown timer. Always launched in parallel with
 * GameScene; it pulls live state from the GameScene each frame via the public
 * `getHudState()` method.
 */
export interface HudPlayerState {
  id: PlayerId;
  name: string;
  lives: number;
  score: number;
  alive: boolean;
  spectator: boolean;
  bombCount: number;
  bombRange: number;
  speedLevel: number;
  hasKick: boolean;
  hasPierce: boolean;
  hasShield: boolean;
  hasRemote: boolean;
}

export interface HudState {
  world: number;
  stage: number;
  isBoss: boolean;
  enemiesLeft: number;
  timeLeftSeconds: number;
  timeLimitSeconds: number;
  p1: HudPlayerState;
  p2: HudPlayerState;
}

const PANEL_H = 56;
const SIDE_PANEL_W = 260;
const ICON_FONT = '16px';

export class HUDScene extends Phaser.Scene {
  private p1Bg!: Phaser.GameObjects.Graphics;
  private p2Bg!: Phaser.GameObjects.Graphics;
  private centerBg!: Phaser.GameObjects.Graphics;
  private p1Text!: Phaser.GameObjects.Text;
  private p2Text!: Phaser.GameObjects.Text;
  private p1Lives!: Phaser.GameObjects.Text;
  private p2Lives!: Phaser.GameObjects.Text;
  private p1Score!: Phaser.GameObjects.Text;
  private p2Score!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private enemyText!: Phaser.GameObjects.Text;
  private muteBtn!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'HUDScene', active: false });
  }

  create() {
    // Translucent panels along the top of the canvas
    this.p1Bg = this.add.graphics().setScrollFactor(0).setDepth(900);
    this.p2Bg = this.add.graphics().setScrollFactor(0).setDepth(900);
    this.centerBg = this.add.graphics().setScrollFactor(0).setDepth(900);

    this.drawPanel(this.p1Bg, 0, 0, SIDE_PANEL_W, PANEL_H, 0x52ff52);
    this.drawPanel(this.p2Bg, CANVAS_W - SIDE_PANEL_W, 0, SIDE_PANEL_W, PANEL_H, 0xff5555);
    this.drawPanel(this.centerBg,
      SIDE_PANEL_W + 6, 0,
      CANVAS_W - 2 * SIDE_PANEL_W - 12, PANEL_H, 0xffffff);

    const baseStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'monospace',
      fontSize: ICON_FONT,
      color: '#fff8d8',
    };

    // P1 panel
    this.p1Text = this.add.text(10, 6, '', { ...baseStyle, color: '#52ff52', fontStyle: 'bold' })
      .setScrollFactor(0).setDepth(1000);
    this.p1Lives = this.add.text(10, 26, '', { ...baseStyle, color: '#ff7a7a' })
      .setScrollFactor(0).setDepth(1000);
    this.p1Score = this.add.text(SIDE_PANEL_W - 10, 26, '', { ...baseStyle, color: '#fff8d8' })
      .setOrigin(1, 0).setScrollFactor(0).setDepth(1000);

    // P2 panel
    this.p2Text = this.add.text(CANVAS_W - SIDE_PANEL_W + 10, 6, '', { ...baseStyle, color: '#ff5555', fontStyle: 'bold' })
      .setScrollFactor(0).setDepth(1000);
    this.p2Lives = this.add.text(CANVAS_W - SIDE_PANEL_W + 10, 26, '', { ...baseStyle, color: '#ff7a7a' })
      .setScrollFactor(0).setDepth(1000);
    this.p2Score = this.add.text(CANVAS_W - 10, 26, '', { ...baseStyle, color: '#fff8d8' })
      .setOrigin(1, 0).setScrollFactor(0).setDepth(1000);

    // Center panel: level + timer + enemies left
    this.levelText = this.add.text(CANVAS_W / 2, 6, '', { ...baseStyle, color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(1000);
    this.timerText = this.add.text(CANVAS_W / 2, 26, '', { ...baseStyle, color: '#ffd166', fontSize: '20px', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(1000);
    this.enemyText = this.add.text(CANVAS_W / 2 + 80, 30, '', { ...baseStyle, color: '#a0ffa0' })
      .setOrigin(0, 0).setScrollFactor(0).setDepth(1000);

    // Audio toggle button — top-right of HUD strip
    this.muteBtn = this.add.text(CANVAS_W - 10, 5, '🔊', {
      fontFamily: 'monospace', fontSize: '15px', color: '#aaa',
    }).setOrigin(1, 0).setScrollFactor(0).setDepth(1001)
      .setInteractive({ useHandCursor: true });
    this.muteBtn.on('pointerover', () => this.muteBtn.setColor('#fff'));
    this.muteBtn.on('pointerout',  () => this.muteBtn.setColor('#aaa'));
    this.muteBtn.on('pointerdown', () => { AudioManager.toggleMute(); });
  }

  update() {
    const game = this.scene.get('GameScene') as GameScene | undefined;
    if (!game || !(game as unknown as { getHudState?: () => HudState }).getHudState) return;
    const state = (game as unknown as { getHudState: () => HudState }).getHudState();
    if (!state) return;

    this.renderPlayer(state.p1, this.p1Text, this.p1Lives, this.p1Score);
    this.renderPlayer(state.p2, this.p2Text, this.p2Lives, this.p2Score);

    const bossTag = state.isBoss ? ' 👑BOSS' : '';
    this.levelText.setText(`LUME ${state.world}-${state.stage}${bossTag}`);

    const t = Math.max(0, Math.ceil(state.timeLeftSeconds));
    const mm = Math.floor(t / 60).toString().padStart(2, '0');
    const ss = (t % 60).toString().padStart(2, '0');
    this.timerText.setText(`⏱ ${mm}:${ss}`);
    const lowTime = t <= 30;
    this.timerText.setColor(lowTime ? '#ff5577' : '#ffd166');
    this.enemyText.setText(`👾 ${state.enemiesLeft}`);
    this.muteBtn.setText(AudioManager.isMuted ? '🔇' : '🔊');
  }

  private renderPlayer(
    p: HudPlayerState,
    nameLbl: Phaser.GameObjects.Text,
    livesLbl: Phaser.GameObjects.Text,
    scoreLbl: Phaser.GameObjects.Text,
  ) {
    const flags = [
      p.hasRemote ? '📡' : '',
      p.hasKick ? '🦵' : '',
      p.hasPierce ? '⚡' : '',
      p.hasShield ? '🛡' : '',
    ].filter(Boolean).join('');
    const stateTag = p.spectator ? ' 👻SPEC' : (p.alive ? '' : ' ☠');
    nameLbl.setText(`${p.name}${stateTag}  💣${p.bombCount} 🔥${p.bombRange} 👟${p.speedLevel} ${flags}`);
    livesLbl.setText(`Vieți: ∞`);
    scoreLbl.setText(String(p.score).padStart(6, '0'));
  }

  private drawPanel(
    g: Phaser.GameObjects.Graphics,
    x: number, y: number, w: number, h: number,
    accent: number,
  ) {
    g.clear();
    g.fillStyle(0x131120, 0.85).fillRect(x, y, w, h);
    g.lineStyle(2, accent, 0.95).strokeRect(x + 1, y + 1, w - 2, h - 2);
  }
}

// Re-export the canvas height so other modules can size panels relative to it
export const HUD_PANEL_HEIGHT = PANEL_H;
